import type { RoundSnapshot } from "../../domain/round/round-types.js";
import type {
  BindSeasonSlotInput,
  SeasonResponse,
  SeasonSlot,
} from "../../shared/contracts/season.js";
import type { RoundJournal } from "../data/round-journal.js";
import type { RoundSyncEngine } from "./round-sync-engine.js";
import type {
  SeasonRoundStartStore,
  PendingSeasonRound,
  SeasonRoundSelection,
  SeasonRoundSetupInput,
} from "./season-round-start-store.js";

export interface SeasonRoundStartOptions {
  accountId: string;
  engine: RoundSyncEngine;
  journal: RoundJournal;
  store: SeasonRoundStartStore;
  api: {
    getSeason(id: string): Promise<SeasonResponse>;
    bindSeasonSlot(
      id: string,
      slotId: string,
      input: BindSeasonSlotInput,
    ): Promise<SeasonResponse>;
    loadRoundSnapshot(id: string): Promise<RoundSnapshot>;
  };
  access(): { accountId: string; authenticated: boolean; online: boolean };
  nextRoundId(): string;
  changed?(): void;
  withLock?<T>(key: string, action: () => Promise<T>): Promise<T>;
}

/** Reserve the agreed Season point before the first round.started command exists. */
export class SeasonRoundStarter {
  private inFlight: Promise<RoundSnapshot> | null = null;
  private inFlightKey: string | null = null;
  public constructor(private readonly options: SeasonRoundStartOptions) {}

  public start(
    input: SeasonRoundSetupInput,
    selection: SeasonRoundSelection,
  ): Promise<RoundSnapshot> {
    return this.run(
      async () => {
        let intent = this.options.store.read(this.options.accountId);
        if (intent && sameSelection(intent, selection)) {
          if (JSON.stringify(intent.input) !== JSON.stringify(input))
            throw new Error(
              "This season setup is already saved. Continue its agreed course and players, or abandon it first.",
            );
        } else {
          if (intent && intent.phase !== "started")
            throw new Error(
              "Continue or abandon the saved season setup first.",
            );
          await this.requireNoOtherRound();
          intent = {
            ...selection,
            storageVersion: 1,
            accountId: this.options.accountId,
            roundId: this.options.nextRoundId(),
            input: structuredClone(input),
            phase: "draft",
            createdAt: new Date().toISOString(),
          };
          this.save(intent);
        }
        return this.continue(intent);
      },
      `start:${selection.seasonId}:${selection.slotId}:${JSON.stringify(input)}`,
    );
  }

  public resume(): Promise<RoundSnapshot> {
    return this.run(() => this.continue(this.requireIntent()), "resume");
  }

  public abandon(): Promise<RoundSnapshot> {
    return this.run(async () => {
      const intent = this.requireIntent();
      intent.phase = "abandoning";
      this.save(intent);
      let snapshot = await this.options.journal.getSnapshot(
        intent.accountId,
        intent.roundId,
      );
      if (!snapshot) {
        const { season } = await this.options.api.getSeason(intent.seasonId);
        this.requireAccess();
        const bound =
          season.slots.find((slot) => slot.id === intent.slotId)?.roundId ===
          intent.roundId;
        snapshot = bound
          ? await this.options.api.loadRoundSnapshot(intent.roundId)
          : await this.options.engine.createRound({
              roundId: intent.roundId,
              ...intent.input,
            });
        await this.options.journal.saveSnapshot(intent.accountId, snapshot);
      }
      snapshot = await this.flush(intent);
      if (snapshot.state !== "abandoned") {
        if (snapshot.state !== "draft" && snapshot.state !== "active")
          throw new Error(
            "This round is already closed. Its season record is preserved.",
          );
        this.requireAccess();
        await this.options.engine.abandonRound(intent.roundId);
        snapshot = await this.flush(intent);
      }
      this.options.store.clear(intent.accountId);
      this.options.changed?.();
      return snapshot;
    }, "abandon");
  }

  private async continue(intent: PendingSeasonRound): Promise<RoundSnapshot> {
    if (intent.phase === "abandoning")
      throw new Error(
        "Finish abandoning this season setup before starting another.",
      );
    // Always re-read the slot, including after lost create/bind/start responses.
    const { season } = await this.options.api.getSeason(intent.seasonId);
    this.requireAccess();
    const slot = season.slots.find(
      (candidate) => candidate.id === intent.slotId,
    );
    if (!slot)
      throw new Error("The selected season slot is no longer available.");
    const alreadyBound = this.boundToIntent(slot, intent);
    await this.loadOrCreate(intent, alreadyBound);
    let snapshot = await this.flush(intent);
    if (alreadyBound) snapshot = await this.refreshBoundRound(intent, snapshot);
    if (snapshot.state === "active" && alreadyBound)
      return this.started(intent, snapshot);
    if (snapshot.state !== "draft")
      throw new Error(
        "A season slot cannot be attached after play has started or the round has closed.",
      );
    if (!alreadyBound) {
      intent.phase = "binding";
      this.save(intent);
      this.requireAccess();
      const response = await this.options.api.bindSeasonSlot(
        intent.seasonId,
        intent.slotId,
        {
          expectedVersion: season.version,
          roundId: intent.roundId,
          contestId: this.contestId(intent),
        },
      );
      this.requireAccess();
      const bound = response.season.slots.find(
        (candidate) => candidate.id === intent.slotId,
      );
      if (!bound || !this.boundToIntent(bound, intent))
        throw new Error(
          "The server did not confirm the reserved season slot. Continue setup to retry.",
        );
    }
    intent.phase = "starting";
    this.save(intent);
    this.requireAccess();
    await this.options.engine.startRound(intent.roundId);
    snapshot = await this.flush(intent);
    if (snapshot.state !== "active")
      throw new Error("The server has not confirmed the season round start.");
    return this.started(intent, snapshot);
  }

  private async loadOrCreate(
    intent: PendingSeasonRound,
    bound: boolean,
  ): Promise<RoundSnapshot> {
    const snapshot = await this.options.journal.getSnapshot(
      intent.accountId,
      intent.roundId,
    );
    if (snapshot) return snapshot;
    this.requireAccess();
    if (!bound)
      return this.options.engine.createRound({
        roundId: intent.roundId,
        ...intent.input,
      });
    const recovered = await this.options.api.loadRoundSnapshot(intent.roundId);
    this.requireAccess();
    if (
      recovered.roundId !== intent.roundId ||
      recovered.scorekeeperId !== intent.accountId
    )
      throw new Error(
        "The saved season round belongs to a different scorekeeper.",
      );
    await this.options.journal.saveSnapshot(intent.accountId, recovered);
    return recovered;
  }

  private async flush(intent: PendingSeasonRound): Promise<RoundSnapshot> {
    this.requireAccess();
    const snapshot = await this.options.engine.flush(intent.roundId);
    this.requireAccess();
    if (
      this.options.engine.getConflicts().length ||
      (await this.options.journal.listQueued(intent.accountId, intent.roundId))
        .length
    )
      throw new Error(
        "Resolve or sync the saved round changes before continuing season setup.",
      );
    return snapshot;
  }

  private async refreshBoundRound(
    intent: PendingSeasonRound,
    local: RoundSnapshot,
  ): Promise<RoundSnapshot> {
    this.requireAccess();
    const current = await this.options.api.loadRoundSnapshot(intent.roundId);
    this.requireAccess();
    if (
      current.roundId !== intent.roundId ||
      current.scorekeeperId !== intent.accountId ||
      current.version < local.version
    )
      throw new Error(
        "The acknowledged season round changed. Refresh its scorekeeper and result before continuing.",
      );
    await this.options.journal.saveSnapshot(intent.accountId, current);
    return current;
  }

  private boundToIntent(slot: SeasonSlot, intent: PendingSeasonRound): boolean {
    if (!slot.roundId) {
      if (slot.state !== "open")
        throw new Error("This season slot is not open for a round.");
      return false;
    }
    if (
      slot.roundId !== intent.roundId ||
      slot.contestId !== this.contestId(intent)
    )
      throw new Error(
        "This season slot is reserved for a different round. The saved setup has not started.",
      );
    return true;
  }

  private contestId(intent: PendingSeasonRound): string {
    if (intent.input.schemaVersion !== 3 || !intent.input.mainContest?.id)
      throw new Error(
        "Season play requires an explicitly agreed main contest.",
      );
    return intent.input.mainContest.id;
  }

  private async requireNoOtherRound(): Promise<void> {
    if (
      await this.options.journal.getLatestRecoverableSnapshot(
        this.options.accountId,
      )
    )
      throw new Error(
        "Finish or abandon and sync the current round before starting a season round.",
      );
    this.requireAccess();
  }

  private started(
    intent: PendingSeasonRound,
    snapshot: RoundSnapshot,
  ): RoundSnapshot {
    this.save({ ...intent, phase: "started" });
    return snapshot;
  }
  private requireIntent(): PendingSeasonRound {
    const intent = this.options.store.read(this.options.accountId);
    if (!intent) throw new Error("There is no saved season setup to continue.");
    return intent;
  }
  private save(intent: PendingSeasonRound): void {
    this.options.store.write(intent);
    this.options.changed?.();
  }
  private requireAccess(): void {
    const access = this.options.access();
    if (
      access.accountId !== this.options.accountId ||
      !access.authenticated ||
      !access.online
    )
      throw new Error(
        "Connect and sign in to this account before starting or continuing a season round.",
      );
  }
  private run(
    action: () => Promise<RoundSnapshot>,
    operation: string,
  ): Promise<RoundSnapshot> {
    if (this.inFlight)
      return this.inFlightKey === operation
        ? this.inFlight
        : Promise.reject(
            new Error(
              "Wait for the current season setup action before choosing another.",
            ),
          );
    const execute = async () => {
      this.requireAccess();
      return action();
    };
    const pending = this.options.withLock
      ? this.options.withLock(
          `fairway-season-start:${this.options.accountId}`,
          execute,
        )
      : execute();
    this.inFlightKey = operation;
    this.inFlight = pending.finally(() => {
      this.inFlight = null;
      this.inFlightKey = null;
    });
    return this.inFlight;
  }
}

function sameSelection(
  left: SeasonRoundSelection,
  right: SeasonRoundSelection,
): boolean {
  return left.seasonId === right.seasonId && left.slotId === right.slotId;
}
